<template>
	<!-- Normal Sidebar with Integrated Settings -->
	<div class="side-nav-container normal">
		<div class="main-nav-section">
			<!-- Main Navigation Section (now collapsible) -->
			<div class="main-section">
				<div class="section-header" @click="toggleSection('main')">
					<h3 class="section-title">{{ $t('navigation') }}</h3>
					<svg 
						class="section-icon chevron" 
						:class="{ 'rotated': sectionStates.main }"
						viewBox="0 0 20 20" 
						fill="currentColor"
					>
						<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
					</svg>
				</div>
				<div class="collapsible-content" :class="{ 'collapsed': !sectionStates.main }">
					<ul class="nav-item-list enhanced">
						<template v-for="item in sideBarNavItems">
							<li
								:key="item.name"
								v-if="canShowNavItem(item) && !hiddenNavItem.includes(item.name)"
								class="nav-item"
							>
								<router-link :to="item.path" class="nav-link">
									<span class="nav-text">{{ $t(item.name) }}</span>
								</router-link>
							</li>
						</template>
					</ul>
				</div>
			</div>
			
			<!-- Settings Content Integrated into Normal Container -->
			<div class="settings-body">
				<!-- Admin Navigation Section -->
				<template v-if="loggedUser && loggedUser.type === 'admin'">
					<div class="admin-section">
						<div class="section-header" @click="toggleSection('admin')">
							<h3 class="section-title">{{ $t('settings') }}</h3>
							<svg 
								class="section-icon chevron" 
								:class="{ 'rotated': sectionStates.admin }"
								viewBox="0 0 20 20" 
								fill="currentColor"
							>
								<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
							</svg>
						</div>
						<div class="collapsible-content" :class="{ 'collapsed': !sectionStates.admin }">
							<ul class="nav-item-list enhanced">
								<template v-for="item in sideBarAdminNavItems">
									<li
										:key="item.name"
										v-if="canShowNavItem(item) && !hiddenNavItem.includes(item.name)"
										class="nav-item"
									>
										<router-link :to="item.path" class="nav-link">
											<span class="nav-text">{{ $t(item.name) }}</span>
										</router-link>
									</li>
								</template>
							</ul>
						</div>
					</div>
				</template>

				<!-- Language Selector Section -->
				<div class="language-section">
					<div class="section-header" @click="toggleSection('language')">
						<h3 class="section-title">{{ $t('language') }}</h3>
						<svg 
							class="section-icon chevron" 
							:class="{ 'rotated': sectionStates.language }"
							viewBox="0 0 20 20" 
							fill="currentColor"
						>
							<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
						</svg>
					</div>
					<div class="collapsible-content" :class="{ 'collapsed': !sectionStates.language }">
						<div class="language-selector-container">
							<Select v-model="selectedLanguage" class="lang-select">
								<Option
									v-for="lang in languages"
									:key="lang.value"
									:label="lang.label"
									:value="lang.value"
									:src="require(`@/assets/languages_flags/${lang.value}.svg`)"
								/>
							</Select>
						</div>
					</div>
				</div>

				<!-- Account Section -->
				<div class="account-section">
					<div class="section-header" @click="toggleSection('account')">
						<h3 class="section-title">{{ $t('account') }}</h3>
						<svg 
							class="section-icon chevron" 
							:class="{ 'rotated': sectionStates.account }"
							viewBox="0 0 20 20" 
							fill="currentColor"
						>
							<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
						</svg>
					</div>
					<div class="collapsible-content" :class="{ 'collapsed': !sectionStates.account }">
						<router-link
							to="/personal-settings"
							class="user-info user-info--settings"
							:title="$t('personal_settings')"
							active-class="user-info--settings-active"
						>
							<div class="user-avatar">
								<span class="avatar-text">{{ loggedUser?.username?.charAt(0)?.toUpperCase() || 'U' }}</span>
							</div>
							<div class="user-details">
								<div class="user-name">{{ loggedUser?.username }}</div>
								<div class="user-role">{{ $t(loggedUser?.type) }}</div>
							</div>
							<svg class="user-info-chevron" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
								<path
									fill-rule="evenodd"
									d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
									clip-rule="evenodd"
								/>
							</svg>
						</router-link>
						<div class="logout-section">
							<button class="logout-btn" @click="logout">
								<svg class="logout-icon" viewBox="0 0 20 20" fill="currentColor">
									<path
										fill-rule="evenodd"
										d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
										clip-rule="evenodd"
									/>
								</svg>
								<span>{{ $t('Logout') }}</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import authService from '@/modules/auth/services/authService'
import Select from '../components/Select.vue'
import Option from '../components/Option'
import { localeData } from '../services/localeService'
import { mapGetters } from 'vuex'
import { sideBarAdminNavItems, sideBarNavItems } from '../consts/consts'

export default {
	data() {
		return {
			alerts: [],
			hiddenNavItem: [],
			sideBarAdminNavItems,
			sideBarNavItems,
			selectedLanguage: localeData.language,
			languages: [
				{ value: 'he', label: 'עברית' },
				{ value: 'en', label: 'English' },
				{ value: 'pt', label: 'Português' },
				{ value: 'ru', label: 'Русский' },
				{ value: 'es', label: 'Español' },
				{ value: 'ar', label: 'العربية' },
				{ value: 'pl', label: 'Polish' },
				{ value: 'uk', label: 'Ukrainian' },
				{ value: 'ro', label: 'Romanian' },
				{ value: 'nl', label: 'Nederlands' },
				{ value: 'cs', label: 'Čeština' },
				{ value: 'ml', label: 'മലയാളം' },
				{ value: 'sk', label: 'Slovenčina' },
				{ value: 'ka', label: 'ქართული' },
				{ value: 'si', label: 'සිංහල' }
			],
			// Section collapse states
			sectionStates: {
				main: true,
				admin: true,
				language: true,
				account: true
			}
		}
	},
	async created() {
		this.alerts = await this.getAlerts()
		// Load saved section states from localStorage if available
		this.loadSectionStatesFromStorage()
	},
	computed: mapGetters(['isRTL', 'loggedUser', 'filterSelected', 'currentSiteInfo']),
	methods: {
		hasPermission(item) {
			const user = this.loggedUser
			if (!user) return false
			return user.roles.includes(item)
		},
		canShowNavItem(item) {
			if (item.buzzzOnly) return !!(this.loggedUser && this.loggedUser.level === 'buzzz')
			if (item.name === 'site_alerts' && this.loggedUser && this.loggedUser.type === 'admin') return true
			if (item.permissions) return item.permissions.some((permission) => this.hasPermission(permission))
			return this.hasPermission(item.permission)
		},
		async hideNavItems() {
			const defaultHiddenNavItems = {
				operation: ['task'],
				survey: ['real_time', 'task', 'planning', 'users'],
				task: ['survey', 'real_time'],
				realtime: ['dashboard', 'survey', 'rooms', 'devices', 'working_area', 'users', 'task'],
				cleandex: [],
				default: ['task']
			}
			this.hiddenNavItem = []
			if (!this.filterSelected || !this.filterSelected.site) {
				this.hiddenNavItem = ['site', 'user']
				return this.hiddenNavItem
			}
			try {
				if (this.filterSelected.site !== this.currentSiteInfo.id) {
					await this.$store.dispatch('loadCurrentSiteInfo')
				}
				const { currentSiteInfo } = this
				if (currentSiteInfo && currentSiteInfo.type) {
					if (currentSiteInfo.type === 'realtime' && this.$route.path !== '/real-time') {
						this.$router.push('/real-time')
					}
					this.hiddenNavItem = defaultHiddenNavItems[currentSiteInfo.type] || defaultHiddenNavItems.default
				}
			} catch (err) {
				console.error('Error fetching site info:')
				this.hiddenNavItem = []
			}
			return this.hiddenNavItem
		},
		getAlerts() {
			return Promise.resolve([1, 2, 3])
		},
		logout() {
			authService.logout()
			this.$router.push('/login')
		},
		toggleLang(language) {
			this.$store.dispatch({ type: 'updateLocale', language })
			this.$store.dispatch('loadFilterOptionsAndSelection')
			this.$root.$i18n.locale = language
		},
		// Method to toggle section visibility
		toggleSection(sectionName) {
			this.sectionStates[sectionName] = !this.sectionStates[sectionName]
			this.saveSectionStatesToStorage()
		},
		// Save section states to localStorage
		saveSectionStatesToStorage() {
			try {
				localStorage.setItem('sidebarSectionStates', JSON.stringify(this.sectionStates))
			} catch (error) {
				console.warn('Could not save section states to localStorage:', error)
			}
		},
		// Load section states from localStorage
		loadSectionStatesFromStorage() {
			try {
				const savedStates = localStorage.getItem('sidebarSectionStates')
				if (savedStates) {
					const parsedStates = JSON.parse(savedStates)
					this.sectionStates = { ...this.sectionStates, ...parsedStates }
				}
			} catch (error) {
				console.warn('Could not load section states from localStorage:', error)
			}
		}
	},
	watch: {
		'filterSelected.site': {
			deep: true,
			immediate: true,
			handler() {
				this.hideNavItems()
			}
		},
		selectedLanguage(value) {
			this.toggleLang(value)
		}
	},
	components: { Select, Option }
}
</script>

<style lang="scss" scoped>
@use '@/styles/components/sideNavbar';
</style>